import type { SelectionRange, SelectionPoint } from "../global";

type WordRun = <T>(batch: (ctx: Word.RequestContext) => Promise<T>) => Promise<T>;

export class SelectionRangeFactory {
  private static readonly QUERIES_BUFFER_SIZE = 2;
  private static readonly TRIM_WEIRD_CHAR = /\u0005/;
  private static readonly START_OF_DOCUMENT = "__START_OF_DOCUMENT__";
  private static readonly END_OF_DOCUMENT = "__END_OF_DOCUMENT__";

  constructor(private _wordRun: WordRun) { }

  async buildRange(signal?: AbortSignal, logError = true): Promise<SelectionRange | null> {
    if (signal?.aborted) {
      return null;
    }

    try {
      return await this._wordRun(async (ctx) => {
        if (signal?.aborted) {
          return null;
        }
        const selection = ctx.document.getSelection();
        return await this.fromRange(ctx, selection, signal);
      });
    } catch (error) {
      if (logError) {
        console.error("[SelectionRangeFactory] Failed to build range:", error);
      }
      return null;
    }
  }

  async fromRange(
    ctx: Word.RequestContext,
    range: Word.Range,
    signal?: AbortSignal
  ): Promise<SelectionRange | null> {
    try {
      signal?.throwIfAborted()

      const startRange = range.getRange("Start");
      const endRange = range.getRange("End");

      await ctx.sync();

      signal?.throwIfAborted()
      const start = await this.getSelectionPoint(ctx, startRange, signal);

      signal?.throwIfAborted()
      if (await this.areIdentical(ctx, startRange, endRange)) {
        return { start };
      }

      signal?.throwIfAborted()
      if (await this.areOnSameParagraph(ctx, startRange, endRange)) {
        const endP = endRange.paragraphs.getFirst();
        const textIndex = await this.getTextIndex(ctx, endP, endRange, signal);
        return { start, end: { ...start, textIndex } };
      }

      signal?.throwIfAborted()
      const end = await this.getSelectionPoint(ctx, endRange, signal);
      return { start, end };
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return null
      }

      throw error
    }
  }

  private async getSelectionPoint(
    ctx: Word.RequestContext,
    r: Word.Range,
    signal?: AbortSignal
  ): Promise<SelectionPoint> {
    const p = r.paragraphs.getFirst();

    const { queries, queryIndex } = await this.getQueries(ctx, p, signal)
    const textIndex = await this.getTextIndex(ctx, p, r, signal)

    return { queries, queryIndex, textIndex };
  }

  private async getQueries(
    ctx: Word.RequestContext,
    p: Word.Paragraph,
    signal?: AbortSignal
  ): Promise<{ queries: string[]; queryIndex: number }> {

    const prefix = await this.getPrefixQueries(ctx, p)
    signal?.throwIfAborted()

    const target = await this.getQuery(ctx, p)
    signal?.throwIfAborted()

    const suffix = await this.getSuffixQueries(ctx, p)
    signal?.throwIfAborted()

    return {
      queries: [...prefix, target, ...suffix],
      queryIndex: prefix.length,
    };
  }

  private async getPrefixQueries(
    ctx: Word.RequestContext,
    target: Word.Paragraph
  ): Promise<string[]> {
    const queries: string[] = [];
    for (let i = 0; i < SelectionRangeFactory.QUERIES_BUFFER_SIZE; i++) {
      try {
        target = target.getPrevious();
        const query = await this.getQuery(ctx, target);
        queries.push(query);
      } catch {
        queries.push(SelectionRangeFactory.START_OF_DOCUMENT);
        i = SelectionRangeFactory.QUERIES_BUFFER_SIZE;
      }
    }
    queries.reverse();
    return queries;
  }

  private async getSuffixQueries(
    ctx: Word.RequestContext,
    p: Word.Paragraph
  ): Promise<string[]> {
    const queries: string[] = [];
    for (let i = 0; i < SelectionRangeFactory.QUERIES_BUFFER_SIZE; i++) {
      try {
        p = p.getNext();
        const query = await this.getQuery(ctx, p);
        queries.push(query);
      } catch {
        queries.push(SelectionRangeFactory.END_OF_DOCUMENT);
        i = SelectionRangeFactory.QUERIES_BUFFER_SIZE;
      }
    }
    return queries;
  }

  private async getQuery(ctx: Word.RequestContext, p: Word.Paragraph): Promise<string> {
    p.load("text");
    await ctx.sync();
    return p.text.replace(SelectionRangeFactory.TRIM_WEIRD_CHAR, "");
  }

  private async getTextIndex(
    ctx: Word.RequestContext,
    p: Word.Paragraph,
    r: Word.Range,
    signal?: AbortSignal
  ): Promise<number> {

    signal?.throwIfAborted()
    const prefix = p.getRange("Start").expandTo(r);
    ctx.load(prefix, { select: "text" });
    await ctx.sync();

    signal?.throwIfAborted()
    return prefix.text.length;
  }

  private async areOnSameParagraph(
    ctx: Word.RequestContext,
    startRange: Word.Range,
    endRange: Word.Range
  ): Promise<boolean> {
    const start = startRange.paragraphs.getFirst();
    start.load("uniqueLocalId");
    const end = endRange.paragraphs.getFirst();
    end.load("uniqueLocalId");
    await ctx.sync();
    return start.uniqueLocalId === end.uniqueLocalId;
  }

  private async areIdentical(
    ctx: Word.RequestContext,
    first: Word.Range,
    second: Word.Range
  ): Promise<boolean> {
    const d = first.compareLocationWith(second);
    await ctx.sync();
    // "Equal" is the string value of Word.LocationRelation.equal
    return d.value === "Equal" || (d.value as any) === 0;
  }
}
